import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  format, parseISO,
} from "date-fns";

const TYPE_COLORS = {
  wedding: "bg-[#C88270]", mehendi: "bg-[#D4A373]", sangeet: "bg-[#829384]",
  haldi: "bg-[#E0A458]", reception: "bg-[#9C6B8E]", engagement: "bg-[#7A9E9F]",
  meeting: "bg-[#8B8B8B]", trial: "bg-[#B5838D]",
};
const SLOT_LANES = ["Morning", "Afternoon", "Evening", "Others"];
const LANES = [...SLOT_LANES, "Trial"];
const SLOT_BADGE = {
  Morning: "bg-[#E0A458]/15 text-[#B07d2f] border-[#E0A458]/40",
  Afternoon: "bg-[#7A9E9F]/15 text-[#4f6f70] border-[#7A9E9F]/40",
  Evening: "bg-[#9C6B8E]/15 text-[#77506b] border-[#9C6B8E]/40",
  Others: "bg-[#8B8B8B]/15 text-[#5C5652] border-[#8B8B8B]/40",
  Trial: "bg-[#B5838D]/15 text-[#8a5966] border-[#B5838D]/50",
};
const SLOT_ABBR = { Morning: "M", Afternoon: "A", Evening: "E", Others: "O", Trial: "Tr" };
const CHIP_CONFIRMED = "bg-[#4E9A6B] text-white";
const CHIP_SOFT = "bg-[#E4B62C] text-[#3d2f05]";
const CHIP_NEUTRAL = "bg-[#C88270] text-white";
const SLOT_BADGE_CONFIRMED = "bg-[#4E9A6B]/15 text-[#357350] border-[#4E9A6B]/45";
const SLOT_BADGE_SOFT = "bg-[#E4B62C]/25 text-[#7a5f0c] border-[#E4B62C]/70 border-dashed";
function weddingStatusOf(ev) { return ev.wedding_status || ev.wedding?.status; }
function isSoftBlock(ev) { return weddingStatusOf(ev) === "soft_block"; }
function chipColor(ev) {
  const s = weddingStatusOf(ev);
  if (s === "soft_block") return CHIP_SOFT;
  if (s === "confirmed") return CHIP_CONFIRMED;
  return CHIP_NEUTRAL;
}
function eventTypeLabel(t) { return t ? t.charAt(0).toUpperCase() + t.slice(1) : ""; }
function evTime(ev) { return ev.ready_by || ev.start_time || ""; }

export default function CalendarPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month"); // month | week | day

  useEffect(() => {
    (async () => {
      try {
        const [evRes, trRes] = await Promise.all([api.get("/events"), api.get("/trials")]);
        const evList = isAdmin
          ? evRes.data
          : evRes.data.filter((ev) => (ev.assigned || []).some((a) => a.team_member_id === user?.id && a.status === "available"));
        // /trials is already scoped to assigned trials for team members
        setEvents([...evList, ...trRes.data]);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, user]);

  const eventsOn = (day) => events.filter((ev) => {
    try { return isSameDay(parseISO(ev.event_date), day); } catch { return false; }
  });
  const eventsInSlot = (day, slot) => eventsOn(day).filter((ev) => {
    if (slot === "Trial") return ev.event_type === "trial";
    if (ev.event_type === "trial") return false;
    const s = ev.slots || [];
    return s.includes(slot) || (s.length === 0 && slot === "Others");
  });

  const prev = () => setCursor(view === "month" ? subMonths(cursor, 1) : view === "week" ? subWeeks(cursor, 1) : subDays(cursor, 1));
  const next = () => setCursor(view === "month" ? addMonths(cursor, 1) : view === "week" ? addWeeks(cursor, 1) : addDays(cursor, 1));

  const label = useMemo(() => {
    if (view === "month") return format(cursor, "MMMM yyyy");
    if (view === "week") {
      const ws = startOfWeek(cursor), we = endOfWeek(cursor);
      return `${format(ws, "dd MMM")} – ${format(we, "dd MMM yyyy")}`;
    }
    return format(cursor, "EEEE, dd MMM yyyy");
  }, [cursor, view]);

  const EventChip = ({ ev, dense }) => (
    <button onClick={() => navigate(`/events/${ev.id}`)} data-testid={`cal-event-${ev.id}`}
      title={`${eventTypeLabel(ev.event_type)} · ${ev.couple_names || ev.title}${evTime(ev) ? " · " + evTime(ev) : ""}`}
      className={`w-full text-left px-2 py-1 rounded text-[11px] ${dense ? "truncate text-[10px]" : ""} ${chipColor(ev)} ${isSoftBlock(ev) ? "ring-1 ring-inset ring-black/10" : ""} hover:opacity-85 transition-opacity`}>
      {isSoftBlock(ev) ? "◆ " : ""}{evTime(ev) ? `${evTime(ev)} ` : ""}{ev.couple_names || ev.title}
    </button>
  );

  const ViewBtn = ({ id, children }) => (
    <button onClick={() => setView(id)} data-testid={`cal-view-${id}`}
      className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${view === id ? "bg-[#2A2421] text-white border-[#2A2421]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>
      {children}
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="calendar-page">
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Schedule</div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421] flex items-center gap-4">
              <CalendarDays size={36} strokeWidth={1.5} className="text-[#C88270]" /> Calendar
            </h1>
            <p className="text-[#5C5652] mt-3">{isAdmin ? "All confirmed & soft-blocked events across the studio." : "Your confirmed events."}</p>
            <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#4E9A6B]" /> Confirmed</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border border-dashed border-[#E4B62C] bg-[#E4B62C]/50" /> Soft Block</span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="cal-view-toggle">
            <ViewBtn id="month">Month</ViewBtn>
            <ViewBtn id="week">Week</ViewBtn>
            <ViewBtn id="day">Day</ViewBtn>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
          <button onClick={prev} data-testid="cal-prev" className="p-2 border border-[#EAE7E0] hover:border-[#C88270] transition-colors"><ChevronLeft size={18} /></button>
          <div className="font-serif text-lg md:text-2xl tracking-tight text-[#2A2421] flex-1 md:flex-none md:min-w-[220px] text-center" data-testid="cal-month-label">{label}</div>
          <button onClick={next} data-testid="cal-next" className="p-2 border border-[#EAE7E0] hover:border-[#C88270] transition-colors"><ChevronRight size={18} /></button>
          <button onClick={() => setCursor(new Date())} data-testid="cal-today" className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-[#EAE7E0] hover:border-[#C88270] transition-colors">Today</button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : view === "month" ? (
        <MonthView cursor={cursor} eventsOn={eventsOn} onOpenDay={(d) => { setCursor(d); setView("day"); }} />
      ) : view === "week" ? (
        <WeekView cursor={cursor} eventsInSlot={eventsInSlot} EventChip={EventChip} />
      ) : (
        <DayView cursor={cursor} eventsInSlot={eventsInSlot} EventChip={EventChip} navigate={navigate} />
      )}
    </div>
  );
}

function MonthView({ cursor, eventsOn, onOpenDay }) {
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) });
  const bookedSlots = (day) => {
    // Map slot -> {hasConfirmed, hasSoft}
    const map = {};
    let trial = { has: false, softOnly: true };
    eventsOn(day).forEach((ev) => {
      const soft = isSoftBlock(ev);
      if (ev.event_type === "trial") {
        trial.has = true;
        if (!soft) trial.softOnly = false;
        return;
      }
      const s = (ev.slots && ev.slots.length) ? ev.slots : ["Others"];
      s.forEach((x) => {
        const m = (map[x] ||= { hasConfirmed: false, hasSoft: false });
        if (soft) m.hasSoft = true; else m.hasConfirmed = true;
      });
    });
    const badges = SLOT_LANES.filter((l) => map[l]).map((l) => ({ slot: l, soft: map[l].hasSoft && !map[l].hasConfirmed }));
    if (trial.has) badges.push({ slot: "Trial", soft: trial.softOnly });
    return badges;
  };
  return (
    <div className="bg-white border border-[#EAE7E0]" data-testid="calendar-grid">
      <div className="grid grid-cols-7 border-b border-[#EAE7E0]">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="py-3 text-center text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, new Date());
          const slots = bookedSlots(day);
          return (
            <div key={day.toISOString()} className={`min-h-[68px] md:min-h-[110px] border-b border-r border-[#EAE7E0] p-1 md:p-2 ${inMonth ? "bg-white" : "bg-[#FAF9F6]"}`} data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}>
              <div className={`text-xs mb-1 md:mb-1.5 ${isToday ? "font-bold text-[#C88270]" : inMonth ? "text-[#2A2421]" : "text-[#B8B2AA]"}`}>{format(day, "d")}</div>
              <div className="flex flex-wrap gap-0.5 md:gap-1">
                {slots.map(({ slot, soft }) => (
                  <button key={slot} onClick={() => onOpenDay(day)} data-testid={`month-slot-${format(day, "yyyy-MM-dd")}-${slot.toLowerCase()}`}
                    title={`${slot}${soft ? " (soft block)" : ""} booked — tap for details`}
                    className={`px-1.5 md:px-2 py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-[0.1em] border ${soft ? SLOT_BADGE_SOFT : SLOT_BADGE_CONFIRMED} hover:opacity-80 transition-opacity`}>
                    <span className="md:hidden">{SLOT_ABBR[slot]}</span>
                    <span className="hidden md:inline">{slot}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, eventsInSlot, EventChip }) {
  const days = eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) });
  return (
    <div className="overflow-x-auto" data-testid="calendar-week">
      <div className="min-w-[820px] bg-white border border-[#EAE7E0]">
        <div className="grid" style={{ gridTemplateColumns: "110px repeat(7, 1fr)" }}>
          <div className="border-b border-r border-[#EAE7E0] py-3" />
          {days.map((day) => {
            const isToday = isSameDay(day, new Date());
            return (
              <div key={day.toISOString()} className="border-b border-r border-[#EAE7E0] py-3 text-center" data-testid={`week-day-${format(day, "yyyy-MM-dd")}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">{format(day, "EEE")}</div>
                <div className={`text-lg font-serif ${isToday ? "text-[#C88270]" : "text-[#2A2421]"}`}>{format(day, "d")}</div>
              </div>
            );
          })}
        </div>
        {LANES.map((slot) => (
          <div key={slot} className="grid" style={{ gridTemplateColumns: "110px repeat(7, 1fr)" }} data-testid={`week-lane-${slot.toLowerCase()}`}>
            <div className="border-b border-r border-[#EAE7E0] p-3 text-[10px] uppercase tracking-[0.25em] text-[#5C5652] bg-[#FAF9F6] flex items-center">{slot}</div>
            {days.map((day) => {
              const evs = eventsInSlot(day, slot);
              return (
                <div key={day.toISOString()} className="border-b border-r border-[#EAE7E0] p-1.5 min-h-[64px] space-y-1" data-testid={`week-cell-${format(day, "yyyy-MM-dd")}-${slot.toLowerCase()}`}>
                  {evs.map((ev) => <EventChip key={ev.id} ev={ev} dense />)}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayView({ cursor, eventsInSlot, EventChip, navigate }) {
  return (
    <div className="space-y-4" data-testid="calendar-day">
      {LANES.map((slot) => {
        const evs = eventsInSlot(cursor, slot);
        return (
          <div key={slot} className="bg-white border border-[#EAE7E0]" data-testid={`day-lane-${slot.toLowerCase()}`}>
            <div className="flex items-center justify-between px-6 py-3 border-b border-[#EAE7E0] bg-[#FAF9F6]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">{slot}</div>
              <div className="text-[10px] text-[#B8B2AA]">{evs.length} event{evs.length === 1 ? "" : "s"}</div>
            </div>
            <div className="p-4">
              {evs.length === 0 ? (
                <div className="text-sm text-[#B8B2AA] italic px-2">Free</div>
              ) : (
                <div className="space-y-3">
                  {evs.map((ev) => (
                    <button key={ev.id} onClick={() => navigate(`/events/${ev.id}`)} data-testid={`day-event-${ev.id}`}
                      className="w-full text-left flex items-center gap-4 p-4 border border-[#EAE7E0] hover:border-[#C88270]/60 transition-colors">
                      <div className={`w-1.5 self-stretch rounded ${chipColor(ev)}`} />
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-0.5">{eventTypeLabel(ev.event_type)}{isSoftBlock(ev) ? " · Soft Block" : ""}</div>
                        <div className="font-serif text-xl tracking-tight text-[#2A2421]">{ev.couple_names || ev.title}</div>
                        <div className="text-sm text-[#5C5652] mt-1 flex flex-wrap gap-x-4">
                          {evTime(ev) && <span>{ev.event_type === "trial" ? "At " : "Ready by "}{evTime(ev)}</span>}
                          {ev.location && <span className="flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
